import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_BASE_URL;

// ==========================================
// 1. Fetch Provider Template (Blueprint)
// ==========================================
export const fetchProviderTemplate = createAsyncThunk(
  'storageConfig/fetchTemplate',
  async (provider, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/storage-configs/template/${provider}`);
      return { provider, template: response.data };
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to fetch template';
      return rejectWithValue(Array.isArray(msg) ? msg.join(', ') : msg);
    }
  }
);

// ==========================================
// 2. Fetch ALL configs (Global Admin View)
// ==========================================
export const fetchAllConfigs = createAsyncThunk(
  'storageConfig/fetchAllConfigs',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/storage-configs`);
      return response.data;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to fetch all configurations';
      return rejectWithValue(Array.isArray(msg) ? msg.join(', ') : msg);
    }
  }
);

// ==========================================
// 3. Fetch all configs for a specific company
// ==========================================
export const fetchCompanyConfigs = createAsyncThunk(
  'storageConfig',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/storage-configs/company`);
      return response.data;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to fetch company configurations';
      return rejectWithValue(Array.isArray(msg) ? msg.join(', ') : msg);
    }
  }
);

// ==========================================
// 4. Initialize/Create a new config
// ==========================================
export const createStorageConfig = createAsyncThunk(
  'storageConfig/createConfig',
  async (configData, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_URL}/storage-configs`, configData);
      // Refresh list after creation - decide which list to refresh based on context
      if (configData.companyId) {
        await dispatch(fetchCompanyConfigs(configData.companyId));
      } else {
        await dispatch(fetchAllConfigs());
      }
      return response.data;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to create configuration';
      return rejectWithValue(Array.isArray(msg) ? msg.join(', ') : msg);
    }
  }
);

// ==========================================
// 5. Update config values (Secrets/Credentials)
// ==========================================
export const updateConfigValue = createAsyncThunk(
  'storageConfig/updateValue',
  async ({ id, configValue, isActive, companyId }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.patch(`${API_URL}/storage-configs/${id}/value`, { 
        configValue, 
        isActive 
      });
      // Refresh list to show updated status/values
      if (companyId) {
        await dispatch(fetchCompanyConfigs(companyId));
      } else {
        await dispatch(fetchAllConfigs());
      }
      return response.data;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to update configuration values';
      return rejectWithValue(Array.isArray(msg) ? msg.join(', ') : msg);
    }
  }
);

const storageConfigSlice = createSlice({
  name: 'storageConfig',
  initialState: {
    configs: [], // This will hold either global or company-specific list
    templates: {},
    status: 'idle',
    error: null,
  },
  reducers: {
    clearConfigError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Template
      .addCase(fetchProviderTemplate.fulfilled, (state, action) => {
        state.templates[action.payload.provider] = action.payload.template;
      })

      // Handle All Loading States
      .addMatcher(
        (action) => action.type.endsWith('/pending') && action.type.startsWith('storageConfig/'),
        (state) => {
          state.status = 'loading';
          state.error = null;
        }
      )

      // Handle Successful Fetches (Both All and Company specific)
      .addMatcher(
        (action) => 
          (action.type === fetchAllConfigs.fulfilled.type || 
           action.type === fetchCompanyConfigs.fulfilled.type),
        (state, action) => {
          state.status = 'succeeded';
          state.configs = action.payload;
          state.error = null;
        }
      )

      // Create & Update Success
      .addMatcher(
        (action) => 
          (action.type === createStorageConfig.fulfilled.type || 
           action.type === updateConfigValue.fulfilled.type),
        (state) => {
          state.status = 'succeeded';
        }
      )

      .addMatcher(
        (action) => action.type.endsWith('/rejected') && action.type.startsWith('storageConfig/'),
        (state, action) => {
          state.status = 'failed';
          state.error = action.payload;
        }
      );
  },
});

export const { clearConfigError } = storageConfigSlice.actions;
export default storageConfigSlice.reducer;